package org.teddapp.project.ui.today

import org.teddapp.project.ui.TeddTokens

import androidx.compose.ui.graphics.Color

import androidx.compose.runtime.ReadOnlyComposable

import androidx.compose.runtime.Composable

import com.tedd.db.Finance_account
import com.tedd.db.Finance_transaction
import com.tedd.db.Focus_session
import com.tedd.db.Task

/**
 * Date du jour pour le dashboard. Aligné sur les autres modules en attendant kotlinx-datetime.
 */
internal val TODAY_HOME_ISO: String
    get() = org.teddapp.project.db.TODAY_ISO
internal val TODAY_HOME_NOW: String
    get() = org.teddapp.project.db.TODAY_ISO + "T09:41"

internal object TodayDate {
    fun fromIso(iso: String): Long {
        val y = iso.substring(0, 4).toInt()
        val m = iso.substring(5, 7).toInt()
        val d = iso.substring(8, 10).toInt()
        val ya = if (m <= 2) y - 1 else y
        val era = (if (ya >= 0) ya else ya - 399) / 400
        val yoe = (ya - era * 400).toLong()
        val mp = if (m > 2) m - 3 else m + 9
        val doy = (153 * mp + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era.toLong() * 146097L + doe - 719468L
    }

    /** 0 = Mon, 6 = Sun. */
    fun dowMondayZero(days: Long): Int {
        val mod = ((days % 7L) + 7L) % 7L
        return ((mod + 3L) % 7L).toInt()
    }
}

internal val DAY_NAMES_FR = listOf("Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche")
internal val MONTH_NAMES_FR = listOf(
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
)

/** Greeting basé sur l'heure du "now" figé. */
internal fun friendlyGreeting(now: String = TODAY_HOME_NOW): String {
    if (now.length < 13) return "Bonjour"
    val hh = now.substring(11, 13).toIntOrNull() ?: return "Bonjour"
    return when (hh) {
        in 0..4 -> "Bonne nuit"
        in 5..11 -> "Bonjour"
        in 12..17 -> "Bon après-midi"
        else -> "Bonsoir"
    }
}

/** Décompose une heure ISO en hh:mm. */
internal fun fmtTime(now: String = TODAY_HOME_NOW): String {
    if (now.length < 16) return now
    return now.substring(11, 16)
}

// ──────────────────────────────────────────────────────────────────
// Aggregators
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// New helpers for the redesigned home (page d'accueil)
// ──────────────────────────────────────────────────────────────────

/**
 * Source d'une tâche pour l'agrégation "pool focus" : OBJ / PRJ / LIB.
 * Couleurs reprises de la palette TEDD du design.
 */
internal enum class TaskSource(val label: String) {
    Obj("OBJ"),
    Prj("PRJ"),
    Lib("LIB");

    /** Repère de l'origine ; le sigle à côté dit la même chose. */
    val color: Color
        @Composable @ReadOnlyComposable
        get() = when (this) {
            Obj -> TeddTokens.ModGoals
            Prj -> TeddTokens.ModPortfolio
            Lib -> TeddTokens.InkMuted
        }
}

internal fun Task.source(): TaskSource = when (parent_kind) {
    "milestone" -> TaskSource.Obj
    "project"   -> TaskSource.Prj
    else        -> TaskSource.Lib
}

/** "vendredi 22 mai" — version courte pour le sous-titre du hero. */
internal fun shortFriendlyToday(iso: String = TODAY_HOME_ISO): String {
    val days = TodayDate.fromIso(iso)
    val dow = TodayDate.dowMondayZero(days)
    val d = iso.substring(8, 10).trimStart('0').ifEmpty { "0" }
    val mIdx = (iso.substring(5, 7).toIntOrNull() ?: 1) - 1
    return "${DAY_NAMES_FR[dow]} $d ${MONTH_NAMES_FR.getOrNull(mIdx) ?: ""}"
}

/** Greeting avec prénom. */
internal fun greetingForUser(name: String = "Léa", now: String = TODAY_HOME_NOW): String =
    "${friendlyGreeting(now)} $name"

/**
 * Pool focus : actions d'objectifs + tâches de projets + tâches libres, due ≤ 7 jours,
 * non supprimées, triées par échéance puis order_index.
 */
internal fun focusPool(tasks: List<Task>, max: Int = 6): List<Task> =
    tasks
        .filter { it.is_deleted == 0L && it.status != "done" }
        .filter { t ->
            val dl = daysLeft(t.due_date)
            dl != null && dl <= 7
        }
        .sortedWith(
            compareBy<Task> { it.due_date ?: "9999-99-99" }.thenBy { it.order_index },
        )
        .take(max)

/** Renvoie le nombre de jours d'écart (peut être négatif si en retard), ou null si pas de date. */
internal fun daysLeft(dueDate: String?, today: String = TODAY_HOME_ISO): Long? {
    if (dueDate.isNullOrBlank() || dueDate.length < 10) return null
    val todayDays = TodayDate.fromIso(today)
    val dueDays = runCatching { TodayDate.fromIso(dueDate.substring(0, 10)) }.getOrNull() ?: return null
    return dueDays - todayDays
}

/** "22 mai" — format compact pour les chips d'échéance. */
internal fun fmtDueShort(dueDate: String?): String {
    if (dueDate.isNullOrBlank() || dueDate.length < 10) return ""
    val d = dueDate.substring(8, 10).trimStart('0').ifEmpty { "0" }
    val mIdx = (dueDate.substring(5, 7).toIntOrNull() ?: 1) - 1
    return "$d ${MONTH_NAMES_FR.getOrNull(mIdx)?.take(3) ?: ""}"
}


/** Convertit un nombre de jours-epoch vers "YYYY-MM-DD". Inverse de TodayDate.fromIso. */
internal fun isoFromEpochDays(days: Long): String {
    // Howard Hinnant civil_from_days
    val z = days + 719468L
    val era = (if (z >= 0) z else z - 146096) / 146097
    val doe = (z - era * 146097).toInt()                    // [0, 146096]
    val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365  // [0, 399]
    val y = (yoe + era * 400).toInt()
    val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)        // [0, 365]
    val mp = (5 * doy + 2) / 153                            // [0, 11]
    val d = doy - (153 * mp + 2) / 5 + 1                    // [1, 31]
    val m = if (mp < 10) mp + 3 else mp - 9                 // [1, 12]
    val finalY = if (m <= 2) y + 1 else y
    val mm = m.toString().padStart(2, '0')
    val dd = d.toString().padStart(2, '0')
    return "$finalY-$mm-$dd"
}

/** Stats finance du mois courant pour un compte donné : (entrées, sorties). */
internal fun monthFinanceStats(
    txns: List<Finance_transaction>,
    accountId: String,
    ym: String = TODAY_HOME_ISO.substring(0, 7),
): Pair<Long, Long> {
    val filtered = txns.filter {
        it.is_deleted == 0L && it.account_id == accountId && it.date.startsWith(ym)
    }
    val income = filtered.filter { it.type == "income" }.sumOf { it.amount }
    val expense = filtered.filter { it.type == "expense" }.sumOf { it.amount }
    return income to expense
}

/** Score global de la journée : part des tâches du jour terminées. */
internal fun computeDayScore(
    todayTasksOpen: Int,
    todayTasksDone: Int,
): Int {
    val total = todayTasksOpen + todayTasksDone
    if (total == 0) return 0
    return (todayTasksDone.toDouble() / total * 100).toInt().coerceIn(0, 100)
}

// ──────────────────────────────────────────────────────────────────
// Insights : séries pour graphs sur le dashboard
// ──────────────────────────────────────────────────────────────────

/** Liste des N derniers jours en ISO, du plus vieux au plus récent (today inclus). */
internal fun lastNDaysIso(n: Int, today: String = TODAY_HOME_ISO): List<String> {
    val end = TodayDate.fromIso(today)
    return (0 until n).map { i -> isoFromEpochDays(end - (n - 1 - i)) }
}


/** Minutes de focus par jour pour chacun des N derniers jours. */
internal fun focusMinutesLastDays(
    n: Int,
    sessions: List<Focus_session>,
): List<Int> {
    val days = lastNDaysIso(n)
    val today = TodayDate.fromIso(TODAY_HOME_ISO)
    return days.map { iso ->
        val dayIdx = TodayDate.fromIso(iso)
        sessions.filter { s ->
            if (s.is_deleted != 0L) return@filter false
            // started_at en ms epoch UTC → jour-epoch
            val sessionDay = s.started_at / 86_400_000L
            // Aligner avec notre référence today : on suppose today = épochDays
            // si jamais started_at est en secondes, fallback ; mais on garde simple.
            val offsetFromToday = today - sessionDay
            val offsetFromIso = today - dayIdx
            offsetFromTodayMatches(offsetFromToday, offsetFromIso, s.started_at, dayIdx)
        }.sumOf { it.focus_minutes.toInt() }
    }
}

/** Vrai si la session correspond au jour ISO ciblé (compatible ms et secondes). */
private fun offsetFromTodayMatches(
    offsetFromTodayMs: Long,
    offsetFromIso: Long,
    startedAt: Long,
    isoEpochDay: Long,
): Boolean {
    val asMsDay = startedAt / 86_400_000L
    val asSecDay = startedAt / 86_400L
    return asMsDay == isoEpochDay || asSecDay == isoEpochDay
}


/**
 * Solde du compte par jour sur les N derniers jours (du plus vieux au plus récent).
 * On part du solde actuel `account.balance` et on remonte en soustrayant le net de chaque
 * journée plus récente que la cible.
 */
internal fun balanceLastDays(
    n: Int,
    account: Finance_account?,
    txns: List<Finance_transaction>,
): List<Long> {
    if (account == null) return List(n) { 0L }
    val days = lastNDaysIso(n)
    val today = TODAY_HOME_ISO
    val txByDate = txns.filter { it.is_deleted == 0L && it.account_id == account.id }
        .groupBy { it.date }
    // balanceEndOfDay[today] = account.balance
    val endOf = HashMap<String, Long>(n + 1)
    endOf[today] = account.balance
    var cursor = account.balance
    // Remonter du plus récent au plus vieux dans la liste
    val ordered = days.reversed()
    var prevDay = today
    for (day in ordered) {
        if (day == today) {
            endOf[day] = account.balance
            continue
        }
        // cursor pointe encore sur prevDay
        val netPrev = txByDate[prevDay].orEmpty().sumOf { tx ->
            if (tx.type == "income") tx.amount else -tx.amount
        }
        cursor -= netPrev
        endOf[day] = cursor
        prevDay = day
    }
    return days.map { endOf[it] ?: account.balance }
}
